import type { Fixture } from '@h2h/shared';
import { Check, Lock } from '../../components/ui/icons';
import type { DuelSideDuelPick } from '../../lib/api';
import { shortRider } from '../../lib/cyclingRound';
import { cn } from '../../lib/cn';
import { signedPoints } from '../../lib/format';
import { backedRider } from './helpers';

/**
 * One rider-vs-rider duel on a cycling stage — the cycling analogue of PickByPick.
 * The header is the matchup (banker duel gets the dark "premium" strip until it
 * resolves, then the winner's surname is highlighted); each side cell shows the
 * rider that player backed, their confidence weight, and the points once settled.
 */
export function DuelByDuel({
  fixture,
  mine,
  theirs,
  rivalName,
  banker,
  stageStarted
}: {
  fixture: Fixture | undefined;
  mine: DuelSideDuelPick | undefined;
  theirs: DuelSideDuelPick | undefined;
  rivalName: string;
  banker: boolean;
  stageStarted: boolean;
}) {
  const aName = fixture?.riderA?.riderName ?? fixture?.homeTeam ?? '—';
  const bName = fixture?.riderB?.riderName ?? fixture?.awayTeam ?? '—';
  const dr = fixture?.duelResult ?? null;
  const settled = fixture?.status === 'SETTLED' && dr?.outcome === 'decided';
  const isVoid = fixture?.status === 'VOID' || dr?.outcome === 'void';
  const winner = settled ? dr?.winner ?? null : null;

  const myPoints = mine && !mine.locked ? mine.points ?? null : null;
  const theirPoints = theirs && !theirs.locked ? theirs.points ?? null : null;
  const iWin = myPoints != null && theirPoints != null && myPoints > theirPoints;
  const theyWin = myPoints != null && theirPoints != null && theirPoints > myPoints;

  // Banker (top-confidence) styling only applies before the duel resolves, exactly
  // like football's "decider" — once settled, the result + points carry the emphasis.
  const bankerLive = banker && !settled && !isVoid;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border bg-card shadow-tile',
        bankerLive ? 'border-[1.5px] border-night shadow-card' : 'border-black/5'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center gap-[9px] border-b p-[11px]',
          bankerLive ? 'border-transparent bg-night' : 'border-black/[0.04] bg-wash'
        )}
      >
        <span className={cn('flex items-center gap-1.5 text-[13px] font-extrabold tracking-snug', bankerLive ? 'text-white' : 'text-ink')}>
          <RiderName surname={shortRider(aName)} won={winner === 'A'} dim={!!settled && winner === 'B'} />
          <span className={cn('text-[11px] font-bold', bankerLive ? 'text-white/45' : 'text-ink4')}>v</span>
          <RiderName surname={shortRider(bName)} won={winner === 'B'} dim={!!settled && winner === 'A'} />
        </span>
        {isVoid ? (
          <span className="rounded-md bg-fill2 px-[7px] py-[2px] text-[10px] font-extrabold tracking-wide text-ink2">VOID</span>
        ) : settled ? (
          <span className="text-[10px] font-bold tracking-wide text-ink3">FT</span>
        ) : bankerLive ? (
          <span className="rounded-md bg-accent/20 px-[7px] py-[2px] text-[10px] font-extrabold tracking-wide text-accent">BANKER</span>
        ) : stageStarted ? (
          <span className="inline-flex items-center gap-[5px] rounded-md bg-accent/20 px-[7px] py-[2px] text-[10px] font-extrabold tracking-wide text-accent">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" /> LIVE
          </span>
        ) : null}
      </div>

      <div className="flex">
        <CyclingSideCell
          label="YOU"
          backed={backedRider(mine, aName, bName)}
          weight={mine?.confidenceWeight}
          points={myPoints}
          locked={!!mine?.locked}
          hasPick={!!mine}
          win={iWin}
          highlight="accent"
        />
        <div className="w-px bg-black/5" />
        <CyclingSideCell
          label={rivalName.split(/\s+/)[0].toUpperCase()}
          backed={backedRider(theirs, aName, bName)}
          weight={theirs?.confidenceWeight}
          points={theirPoints}
          locked={!!theirs?.locked}
          hasPick={!!theirs}
          win={theyWin}
          highlight="rival"
        />
      </div>
    </div>
  );
}

function RiderName({ surname, won, dim }: { surname: string; won: boolean; dim: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1', won && 'text-accent', dim && 'opacity-45')}>
      {surname}
      {won && <Check width={12} height={12} strokeWidth={3} className="text-accent" />}
    </span>
  );
}

function CyclingSideCell({
  label,
  backed,
  weight,
  points,
  locked,
  hasPick,
  win,
  highlight
}: {
  label: string;
  backed: string | null;
  weight?: number;
  points: number | null;
  locked: boolean;
  hasPick: boolean;
  win: boolean;
  highlight: 'accent' | 'rival';
}) {
  return (
    <div
      className={cn(
        'flex-1 p-[14px]',
        win && highlight === 'accent' && 'bg-accent/[0.06]',
        win && highlight === 'rival' && 'bg-[#5B6EE1]/[0.07]'
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'text-[10px] font-extrabold tracking-wide',
            win ? (highlight === 'accent' ? 'text-accent-ink' : 'text-[#5B6EE1]') : 'text-ink2'
          )}
        >
          {label}
        </span>
        {win && <Check width={14} height={14} strokeWidth={3} className={highlight === 'accent' ? 'text-accent' : 'text-[#5B6EE1]'} />}
      </div>
      {locked ? (
        <div className="mt-2 flex items-center gap-[6px] text-ink2">
          <Lock width={13} height={13} strokeWidth={2.2} />
          <span className="text-[12px] font-semibold">Reveals at start</span>
        </div>
      ) : !hasPick || !backed ? (
        <div className="mt-2 text-[12px] font-semibold text-ink3">No pick yet</div>
      ) : (
        <>
          <div className="mt-2 text-[12px] font-bold text-ink">
            {backed}
            {weight != null ? <span className="font-semibold text-ink2"> · ×{weight}</span> : null}
          </div>
          {points != null && (
            <div
              className={cn(
                'num mt-1 text-[18px] font-extrabold',
                highlight === 'accent' ? 'text-accent-ink' : 'text-[#5B6EE1]'
              )}
            >
              {signedPoints(points)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
